import * as React from 'react';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { ISharingInformation, ISharingWizardProps } from '../../controls/shareActionControl';
import { CancelError } from '../../utilities/async/CancelError';
import * as SharingCalloutStyles from './SharingCallout.scss';
import { injectInitialProps } from '../../utilities/components/Props';
import { IWithWizardInjectedProps } from '../decorators/withWizard';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { displayNameFacet } from '../../facets/ItemFacets';
import { ITheme, getTheme } from 'office-ui-fabric-react/lib/Styling';
import { styled, IPropsWithStyles } from 'office-ui-fabric-react/lib/Utilities';
import { IActionStateProps, WithAction } from '../withAction/WithAction';

const COPY_LINK_MODE = 2;
const COPY_LINK_STRING = '&mode=copy';

export interface ISharingCalloutState {
    sharingInformation?: ISharingInformation;
    isSharingDialogLoaded: boolean;
    didSharingHappen: boolean;
    iframeSource: string;
    isOpen: boolean;
    iframeHeight?: number;
    inputElement: HTMLInputElement | null;
    formElement: HTMLFormElement | null;
}

const INITIAL_STATE: ISharingCalloutState = {
    sharingInformation: undefined,
    isSharingDialogLoaded: false,
    didSharingHappen: false,
    iframeSource: '#',
    isOpen: true,
    iframeHeight: 0,
    inputElement: null,
    formElement: null,
};

export interface ISharingCalloutBaseProps extends ISharingWizardProps {
    itemName: string;
    calloutTarget?: HTMLElement;
    theme?: ITheme;
    openManageAccess?: () => void;
}

// Callout that contains an iframed sharing dialog integrated using this documentation:
// https://onedrive.visualstudio.com/OneDriveWeb/
// _git/odsp-common?path=%2Fodsp-shared-sharing%2Fdocs%2Fintegration.md&version=GBmaster&_a=preview
class SharingCalloutBase extends React.PureComponent<ISharingCalloutBaseProps & IPropsWithStyles<{}, {}>, ISharingCalloutState> {

    constructor(props: ISharingCalloutBaseProps) {
        super(props);
        this.state = INITIAL_STATE;
    }

    public componentWillMount(): void {
        this._getSharingInformation();
    }

    public componentDidMount(): void {
        window.addEventListener('message', this._listenForMessages, false);
        this._update(INITIAL_STATE);
    }

    public componentWillUnmount(): void {
        window.removeEventListener('message', this._listenForMessages, false);
    }

    public componentDidUpdate(previousProps: ISharingCalloutBaseProps, previousState: ISharingCalloutState): void {
        this._update(previousState);
    }

    public render(): JSX.Element | null {
        const {
            isSharingDialogLoaded,
            iframeSource,
            iframeHeight,
            isOpen
        }: ISharingCalloutState = this.state;

        if (isOpen) {
            return (
                <Callout
                    onDismiss={ this._onClose }
                    target={ this.props.calloutTarget }
                    preventDismissOnScroll={ true }
                    isBeakVisible={ false }
                >
                    <div className={ SharingCalloutStyles.sharingContents }>
                        { !isSharingDialogLoaded &&
                            <div className={ SharingCalloutStyles.shareSpinner }>
                                <Spinner size={ SpinnerSize.large } />
                            </div>
                        }
                        <form
                            ref={ this._onFormRef }
                            target='shareFrame'
                            method='post'
                            action='https://www.odwebp.svc.ms/share'
                            style={ { display: 'none' } }
                        >
                            <input ref={ this._onInputRef } type='submit' />
                        </form>
                        <iframe
                            id='shareFrame'
                            name='shareFrame'
                            src={ iframeSource }
                            style={ {
                                visibility: isSharingDialogLoaded ? 'visible' : 'hidden',
                                height: iframeHeight + 'px'
                            } }
                        />
                    </div>
                </Callout>
            );
        } else {
            return null;
        }
    }

    @autobind
    private _listenForMessages(sharingEvent: MessageEvent): void {
        let sharingEventData;
        try {
            sharingEventData = JSON.parse(sharingEvent.data);
        } catch (e) {
            return;
        }

        // Determine what the message is and respond appropriately.
        const sharingEventName = sharingEventData.name;
        if (sharingEventName === 'share_resize' && sharingEventData.height) {
            this.setState({ iframeHeight: sharingEventData.height });
            // Bug in POST messages means sharingEventData.width comes back as zero.
            // Using min-width 320px instead.
        } else if (sharingEventName === 'share_ready') {
            this.setState({ isSharingDialogLoaded: true });
        } else if (sharingEventName === 'share_error') {
            if (sharingEventData.error) {
                this.props.reject(sharingEventData.error);
            } else {
                this.props.reject(new Error('Error in sharing dialog'));
            }
        } else if (sharingEventName === 'share_completed') {
            this.setState({ didSharingHappen: true });
        } else if (sharingEventName === 'share_dismiss') {
            this._onClose();
        } else if (sharingEventName === 'share_manageAccessClicked') {
            const {
                openManageAccess
            } = this.props;

            if (openManageAccess) {
                openManageAccess();
            }
        }
    }

    @autobind
    private _onClose(): void {
        // Check if the user shared the item or canceled the dialog without sharing
        if (this.state.didSharingHappen) {
            this.props.resolve();
        } else {
            this.props.reject(new CancelError());
        }
        this.setState({ isOpen: false });
    }

    @autobind
    private _onInputRef(inputElement: HTMLInputElement | null): void {
        this.setState({
            inputElement: inputElement
        });
    }

    @autobind
    private _onFormRef(formElement: HTMLFormElement | null): void {
        this.setState({
            formElement: formElement
        });
    }

    private async _getSharingInformation(): Promise<void> {
        try {
            const sharingInformation = await this.props.getSharingInformation();

            this.setState({
                sharingInformation: sharingInformation
            });
        } catch (e) {
            this.props.reject(e);
        }
    }

    private _update(previousState: ISharingCalloutState): void {
        const {
            sharingInformation
        } = this.state;

        if (sharingInformation && !previousState.sharingInformation) {
            const {
                authToken,
                webAbsoluteUrl,
                serverRelativeItemUrl,
                listUrl,
                cookieAuthIframeUrl
            } = sharingInformation;
            /**
             * We use a different URL for the iframe depending on whether
             * we are using token based authentication or cookie based
             * authentication.
             */
            if (authToken && webAbsoluteUrl && serverRelativeItemUrl && listUrl) {
                this._useAuthToken(sharingInformation);
            } else if (cookieAuthIframeUrl) {
                // Add extra query parameter if we're in copy link mode
                let combinedUrl = (this.props.mode === COPY_LINK_MODE) ?
                    cookieAuthIframeUrl + COPY_LINK_STRING :
                    cookieAuthIframeUrl;
                this.setState({
                    iframeSource: combinedUrl
                });
            } else {
                this.props.reject(new Error('Missing sharing information'));
            }
        }
    }

    private _useAuthToken(sharingInformation: ISharingInformation): void {
        // Get form
        const form = this.state.formElement;
        const submission = this.state.inputElement;

        if (form && submission) {
            // Construct ISharingContextInformation object.
            const sharingContextInformation = {
                authToken: sharingInformation.authToken,
                webAbsoluteUrl: sharingInformation.webAbsoluteUrl,
                itemName: this.props.itemName,
                clientId: this.props.getClientId(),
                theme: this.props.theme || getTheme(),
                customLabeledAudienceConfig: this.props.customLabeledAudienceConfig,
                defaultRecipients: this.props.defaultRecipients,
                serverRelativeItemUrl: sharingInformation.serverRelativeItemUrl,
                listUrl: sharingInformation.listUrl,
                mode: this.props.mode
            };

            // Request page with POST data.
            const input = document.createElement('input');
            input.type = 'text';
            input.name = 'context';
            input.value = JSON.stringify(sharingContextInformation);
            form.appendChild(input);
            submission.click();
        }
    }
}

type ISharingCalloutActionProps = ISharingWizardProps & Pick<ISharingCalloutBaseProps, 'itemName' | 'calloutTarget'>;

class SharingCalloutAction extends React.PureComponent<
    ISharingCalloutActionProps & IPropsWithStyles<{}, {}>> {
    constructor(props: ISharingCalloutActionProps) {
        super(props);
    }

    public render(): JSX.Element | null {
        const { manageAccessAction, ...props } = this.props;
        return manageAccessAction ?
            (
                <WithAction action={ manageAccessAction }>
                    {
                        ({ isAvailable, execute }: IActionStateProps) => {
                            const openManageAccessAction = isAvailable ? () => {
                                execute({});
                            } : undefined;
                            return (<SharingCalloutBase
                                openManageAccess={ openManageAccessAction }
                                { ...props }
                            />);
                        }
                    }
                </WithAction>
            ) : (<SharingCalloutBase { ...props } />);
    }
}

export const SharingCallout: React.ComponentType<ISharingWizardProps> =
    connectToItemCache((itemCache: IItemCache, props: Pick<ISharingWizardProps, 'itemKey'>):
        Pick<ISharingCalloutBaseProps, 'itemName'> => {
        const { itemKey } = props;
        const {
            displayName
        } = itemCache.demandItem({
            displayName: displayNameFacet,
        }, itemKey);

        return {
            itemName: displayName || ''
        };
    }, (dispatch: IItemCacheDispatch) => ({}))(
        injectInitialProps((props: Pick<IWithWizardInjectedProps, 'onExecuteProps'> & Pick<ISharingWizardProps, 'mode'>):
            Pick<ISharingCalloutBaseProps, 'calloutTarget'> => {
            const {
                onExecuteProps
            } = props;

            let targetElement = (onExecuteProps && onExecuteProps.event) ? onExecuteProps.event.target as HTMLElement : undefined;
            if (targetElement && targetElement.className.indexOf('ms-ContextualMenu-itemText') !== -1) {
                // target is contextMenu share or CopyLink button it goes away, should set target to commandbar button
                const shareButton = document.getElementsByName(props.mode === 1 ? 'Share' : 'Copy link')[0];
                if (shareButton && shareButton.className.indexOf('ms-Button--commandBar') !== -1) {
                    targetElement = shareButton;
                }
            }

            return {
                calloutTarget: targetElement
            };
        })(styled(SharingCalloutAction, (props: ISharingCalloutActionProps) => ({})))
    );
